import React, { useState, useEffect, useMemo, JSX } from 'react';
import axios from 'axios';
import { asList } from '../../../utils/apiList';
import MainLayout from '../../../components/layout/MainLayout';
import TeacherSidebar from '../sidebar/TeacherSidebar';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaSync, FaBook } from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import './TeacherUnitsPage.css';
import '../../admin/Admin.css';


import { API_URL } from '../../../config';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
interface Unit {
  id: number;
  name: string;
  description: string;
  subject_id: number;
  created_by: number;
  created_at: string;
  updated_at: string | null;
}

interface FormData {
  name: string;
  description: string;
  subject_id: string;
}

// Add interfaces for hierarchical filtering
interface Class {
  id: number;
  name: string;
  description: string;
}

interface Stream {
  id: number;
  name: string;
  description: string;
  class_id: number;
  class_: Class;
}

interface Subject {
  id: number;
  name: string;
  code: string;
  description: string;
  stream_id: number;
  stream: Stream;
}

const TeacherUnitsPage: React.FC = () => {
  const { token, logout } = useAuth();
  const [units, setUnits] = useState<Unit[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    subject_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  
  // Add filter states
  const [selectedClass, setSelectedClass] = useState<number>(0);
  const [selectedStream, setSelectedStream] = useState<number>(0);
  const [selectedSubject, setSelectedSubject] = useState<number>(0);
  
  // Add modal filter states
  const [modalSelectedClass, setModalSelectedClass] = useState<number>(0);
  const [modalSelectedStream, setModalSelectedStream] = useState<number>(0);
  const [modalFilteredStreams, setModalFilteredStreams] = useState<Stream[]>([]);
  const [modalFilteredSubjects, setModalFilteredSubjects] = useState<Subject[]>([]);
  
  // Success notification state
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | JSX.Element>('');

  // Add delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    unitId: number | null;
    unitName: string;
  }>({
    show: false,
    unitId: null,
    unitName: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [unitsResponse, subjectsResponse, streamsResponse, classesResponse] = await Promise.all([
          axios.get(`${API_URL}/api/units/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          axios.get(`${API_URL}/api/subjects/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          axios.get(`${API_URL}/api/streams/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          axios.get(`${API_URL}/api/classes/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          })
        ]);
        setUnits(asList(unitsResponse.data));
        setSubjects(asList(subjectsResponse.data));
        setStreams(asList(streamsResponse.data));
        setClasses(asList(classesResponse.data));
        setError(null);
      } catch (err) {
        setMessage({ text: 'Failed to load data. Please try again.', type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Hide success message after 3 seconds
  React.useEffect(() => {
    if (message.text && message.type === 'success') {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear form errors when user starts typing
    if (formErrors[name]) {
      const updatedErrors = { ...formErrors };
      delete updatedErrors[name];
      setFormErrors(updatedErrors);
    }
    // Clear message when user starts typing
    if (message.text && message.type === 'error') {
      setMessage({ text: '', type: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ text: '', type: '' });
    try {
      const url = selectedUnit
        ? `${API_URL}/api/units/${selectedUnit.id}`
        : `${API_URL}/api/units/`;
      const method = selectedUnit ? 'PUT' : 'POST';
      const payload = {
        name: formData.name,
        description: formData.description,
        subject_id: Number(formData.subject_id)
      };
      const response = await axios({
        method,
        url,
        data: payload,
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 200 || response.status === 201) {
        const updatedUnit = response.data;
        setUnits(prevUnits =>
          selectedUnit
            ? prevUnits.map(unit => unit.id === updatedUnit.id ? updatedUnit : unit)
            : [...prevUnits, updatedUnit]
        );
        setOpenDialog(false);
        setSelectedUnit(null);
        resetForm();
        setSuccessMessage(selectedUnit ? 'Unit updated successfully!' : 'Unit created successfully!');
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 2000);
      } else {
        throw new Error('Failed to save unit');
      }
    } catch (err) {
      console.error('Error saving unit:', err);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 409) {
          // Handle duplicate unit name validation error
          const errorMessage = err.response?.data?.detail || 'A unit with this name already exists for this subject. Please use a different name.';
          // Set form error for the name field (only in modal, not on main page)
          setFormErrors({ name: errorMessage });
        } else if (err.response?.data?.detail) {
          setMessage({ text: err.response.data.detail, type: 'error' });
        } else {
          setMessage({ text: err instanceof Error ? err.message : 'Failed to save unit', type: 'error' });
        }
      } else {
        setMessage({ text: err instanceof Error ? err.message : 'Failed to save unit', type: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (unit: Unit) => {
    setSelectedUnit(unit);
    
    // Find the subject for this unit
    const subject = subjects.find(s => s.id === unit.subject_id);
    if (subject) {
      // Find the stream for this subject
      const stream = streams.find(s => s.id === subject.stream_id);
      if (stream) {
        // Set the class
        setModalSelectedClass(stream.class_id);
        // Filter and set streams for this class
        const classStreams = streams.filter(s => s.class_id === stream.class_id);
        setModalFilteredStreams(classStreams);
        // Set stream
        setModalSelectedStream(stream.id);
        // Filter and set subjects for this stream
        const streamSubjects = subjects.filter(s => s.stream_id === stream.id);
        setModalFilteredSubjects(streamSubjects);
      }
    }

    setFormData({
      name: unit.name,
      description: unit.description || '',
      subject_id: String(unit.subject_id)
    });
    setOpenDialog(true);
  };

  // Handle delete confirmation
  const handleDeleteClick = (unit: Unit) => {
    setDeleteConfirmation({
      show: true,
      unitId: unit.id,
      unitName: unit.name
    });
  };
  
  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteConfirmation.unitId) return;
    
    try {
      const response = await axios.delete(`${API_URL}/api/units/${deleteConfirmation.unitId}`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 204 || response.status === 307) {
        setUnits(prevUnits => prevUnits.filter(unit => unit.id !== deleteConfirmation.unitId));
        setSuccessMessage(<span dangerouslySetInnerHTML={{ __html: `Unit "${deleteConfirmation.unitName}" deleted successfully` }}/>);
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 4000);
      } else if (response.status === 200 && response.data && response.data.id) {
        setUnits(prevUnits => prevUnits.filter(unit => unit.id !== response.data.id));
        setSuccessMessage(<span dangerouslySetInnerHTML={{ __html: `Unit "${deleteConfirmation.unitName}" deleted successfully` }}/>);
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 4000);
      } else {
        throw new Error('Failed to delete unit');
      }
      
      // Close confirmation dialog
      setDeleteConfirmation({ show: false, unitId: null, unitName: '' });

    } catch (err) {
      console.error('Error deleting unit:', err);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          setMessage({ text: 'Unauthorized: Please login again', type: 'error' });
        } else if (err.response?.status === 403) {
          setMessage({ text: 'Forbidden: You do not have permission to delete units', type: 'error' });
        } else if (err.response?.status === 404) {
          setMessage({ text: 'Unit not found', type: 'error' });
        } else if (err.response?.data?.detail) {
          setMessage({ text: err.response.data.detail, type: 'error' });
        } else {
          setMessage({ text: `Failed to delete unit: ${err.response?.data?.message || err.message}`, type: 'error' });
        }
      } else {
        setMessage({ text: 'Failed to delete unit', type: 'error' });
      }
    }
  };
  
  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, unitId: null, unitName: '' });
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', subject_id: '' });
    setSelectedUnit(null);
    setFormErrors({});
    setModalSelectedClass(0);
    setModalSelectedStream(0);
    setModalFilteredStreams([]);
    setModalFilteredSubjects([]);
    // Clear message when form is reset
    if (message.text && message.type === 'error') {
      setMessage({ text: '', type: '' });
    }
  };

  // Filter handlers
  const handleClassChange = (value: number) => {
    setSelectedClass(value);
    setSelectedStream(0);
    setSelectedSubject(0);
  };

  const handleStreamChange = (value: number) => {
    setSelectedStream(value);
    setSelectedSubject(0);
  };

  const handleSubjectChange = (value: number) => {
    setSelectedSubject(value);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedClass(0);
    setSelectedStream(0);
    setSelectedSubject(0);
    setSortBy('name-asc');
  };

  // Handle modal class change
  const handleModalClassChange = (value: number) => {
    setModalSelectedClass(value);
    setModalSelectedStream(0);
    setFormData(prev => ({ ...prev, subject_id: '' }));
    if (value) {
      const filteredStreams = streams.filter(stream => stream.class_id === value);
      setModalFilteredStreams(filteredStreams);
    } else {
      setModalFilteredStreams([]);
    }
    setModalFilteredSubjects([]);
  };

  // Handle modal stream change
  const handleModalStreamChange = (value: number) => {
    setModalSelectedStream(value);
    setFormData(prev => ({ ...prev, subject_id: '' }));
    if (value) {
      const filteredSubjects = subjects.filter(subject => subject.stream_id === value);
      setModalFilteredSubjects(filteredSubjects);
    } else {
      setModalFilteredSubjects([]);
    }
  };

  const fetchUnits = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/units/`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      setUnits(asList(response.data));
    } catch (err) {
      setMessage({ text: 'Failed to refresh units', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Filtered data
  const filteredStreams = selectedClass ? streams.filter(stream => stream.class_id === selectedClass) : streams;
  const filteredSubjects = selectedStream ? subjects.filter(subject => subject.stream_id === selectedStream) : subjects;

  // Filter and sort units
  const filteredAndSortedUnits = React.useMemo(() => {
    let result = [...units];
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(unit => 
        unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (unit.description && unit.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by class (through subjects -> streams -> classes)
    if (selectedClass) {
      result = result.filter(unit => {
        const subject = subjects.find(s => s.id === unit.subject_id);
        return subject?.stream?.class_?.id === selectedClass;
      });
    }
    
    // Filter by stream (through subjects -> streams)
    if (selectedStream) {
      result = result.filter(unit => {
        const subject = subjects.find(s => s.id === unit.subject_id);
        return subject?.stream?.id === selectedStream;
      });
    }
    
    // Filter by subject
    if (selectedSubject) {
      result = result.filter(unit => unit.subject_id === selectedSubject);
    }
    
    // Sort
    switch (sortBy) {
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'date-asc':
        result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'date-desc':
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      default:
        break;
    }
    
    return result;
  }, [units, searchTerm, sortBy, selectedClass, selectedStream, selectedSubject, subjects]);

  const pagination = usePagination(filteredAndSortedUnits, 50);

  return (
    <MainLayout user={{ name: '', role: 'teacher' }} onLogout={logout} sidebarContent={<TeacherSidebar />}> 
      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="fixed top-4 right-4 z-[9999] max-w-sm bg-green-50 border border-green-200 text-green-800 px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-fade-in">
          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <span className="font-medium">{successMessage}</span>
          <button
            onClick={() => setShowSuccessNotification(false)}
            className="ml-auto text-green-600 hover:text-green-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="subject-page-root">
        {/* Header Section */}
        <div className="px-8 py-6 flex justify-between items-start">
          <div className="flex flex-col items-start">
            <div className="flex items-center ">
              <h1 className="admin-title">Manage Units</h1>
            </div>
          </div>
          <button
            onClick={() => setOpenDialog(true)}
            className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-[#111827] hover:bg-gray-800 text-white font-kumbh text-base font-semibold shadow-lg transition active:scale-95 min-w-[160px]"
            style={{ fontFamily: 'Inter, sans-serif', boxShadow: '0 8px 24px #0050FF22' }}
            disabled={isLoading}
          >
            Add Unit
          </button>
        </div>

        {/* Filters & Actions Bar */}
        <div className="w-full flex justify-center mt-4 ">
          <div className="bg-white rounded-lg flex flex-col md:flex-row items-center justify-between p-6 w-full max-w-7xl gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0 w-full md:w-auto">
              <div className="relative min-w-[180px]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {FaSearch({})}
                </span>
                <input
                  type="text"
                  placeholder="Search units.."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 shadow focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white text-sm w-full font-kumbh placeholder-gray-400"
                  style={{ boxShadow: '0 2px 6px #0001' }}
                />
              </div>
             
              <select
                value={selectedClass}
                onChange={e => handleClassChange(Number(e.target.value))}
                className="rounded-lg border border-gray-200 py-2.5 px-3 text-sm font-kumbh bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 shadow min-w-[120px]"
                style={{ boxShadow: '0 2px 6px #0001' }}
              >
                <option value={0}>All Classes</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </select>
              <select
                value={selectedStream}
                onChange={e => handleStreamChange(Number(e.target.value))}
                className="rounded-lg border border-gray-200 py-2.5 px-3 text-sm font-kumbh bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 shadow min-w-[120px]"
                style={{ boxShadow: '0 2px 6px #0001' }}
                disabled={!selectedClass}
              >
                <option value={0}>All Streams</option>
                {filteredStreams.map(stream => (
                  <option key={stream.id} value={stream.id}>{stream.name}</option>
                ))}
              </select>
              <select
                value={selectedSubject}
                onChange={e => handleSubjectChange(Number(e.target.value))}
                className="rounded-lg border border-gray-200 py-2.5 px-3 text-sm font-kumbh bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 shadow min-w-[120px]"
                style={{ boxShadow: '0 2px 6px #0001' }}
                disabled={!selectedStream}
              >
                <option value={0}>All Subjects</option>
                {filteredSubjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </select>

              <button
                onClick={handleResetFilters}
                className="rounded-lg border border-gray-200 py-2.5 px-3 text-sm font-kumbh bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 shadow min-w-[120px] font-medium transition active:scale-95"
                style={{ boxShadow: '0 2px 6px #0001' }}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-grow overflow-auto p-8 pt-2">
          <div className="max-w-7xl mx-auto">
            {message.text && (
              <div className={`mb-4 p-4 rounded-md ${
                message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {message.text}
              </div>
            )}
            {isLoading && !openDialog && (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
            {!isLoading && (
              <div className="bg-white shadow rounded-lg overflow-x-auto mt-3">
                <table className="min-w-full divide-y divide-gray-100 font-kumbh">
                  <thead className="bg-white">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[30%]">NAME</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[30%]">DESCRIPTION</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[20%]">SUBJECT</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[12%]">ACTION</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {pagination.paginatedData.map((unit) => {
                      const subject = subjects.find(s => s.id === unit.subject_id);
                      return (
                        <tr key={unit.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-3 text-center whitespace-nowrap text-sm text-gray-900">
                            <div className="truncate max-w-[250px]" title={unit.name}>{unit.name}</div>
                          </td>
                          <td className="px-6 py-3 text-center text-sm text-gray-600">
                            <div className="truncate max-w-[250px]" title={unit.description || '-'}>{unit.description || '-'}</div>
                          </td>
                          <td className="px-6 py-3 text-sm text-center text-gray-600">
                            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 truncate max-w-[120px]" title={subject?.name || 'No Subject'}>
                              {subject?.name || 'No Subject'}
                            </span>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end items-center gap-3 min-w-[120px]">
                              <button
                                onClick={() => handleEdit(unit)}
                                className="inline-flex items-center px-3 py-1 text-sm font-medium text-[#111827] bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isLoading}
                              >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <button 
                                onClick={() => handleDeleteClick(unit)}
                                className="inline-flex items-center px-3 py-1 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isLoading}
                              >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredAndSortedUnits.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-8 py-4 text-center text-sm text-gray-400 font-kumbh">
                          {searchTerm ? 'No units match your search criteria.' : 'No units available. Add your first unit!'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <Pagination {...pagination} onPageChange={pagination.setCurrentPage} onItemsPerPageChange={pagination.setItemsPerPage} />
              </div>
            )}
          </div>
        </div>

        {/* Modal Dialog */}
        {openDialog && (
          <div className="subject-modal-overlay">
            <div className="subject-modal-box" style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh', width: '800px', maxWidth: '90vw' }}>
              {/* Modal Header */}
              <div className="subject-modal-title" style={{ flex: 'none', borderBottom: '1px solid #eee', paddingBottom: 12, marginBottom: 0 }}>
                {selectedUnit ? 'Edit Unit' : 'Add Unit'}
              </div>
              {/* Scrollable Form Fields */}
              <form id="create-unit-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 0', marginBottom: 0 }}>
                  <div className="grid grid-cols-2 gap-6 px-4">
                    <div>
                      <label htmlFor="modal-class" className="subject-modal-form-label">Class*</label>
                      <select
                        id="modal-class"
                        value={modalSelectedClass}
                        onChange={(e) => handleModalClassChange(Number(e.target.value))}
                        className="subject-modal-input"
                        required
                      >
                        <option value={0}>Select a class</option>
                        {classes.map(cls => (
                          <option key={cls.id} value={cls.id}>{cls.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="modal-stream" className="subject-modal-form-label">Stream*</label>
                      <select
                        id="modal-stream"
                        value={modalSelectedStream}
                        onChange={(e) => handleModalStreamChange(Number(e.target.value))}
                        className="subject-modal-input"
                        required
                        disabled={!modalSelectedClass}
                      >
                        <option value={0}>Select a stream</option>
                        {modalFilteredStreams.map(stream => (
                          <option key={stream.id} value={stream.id}>{stream.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="new-subject" className="subject-modal-form-label">Subject*</label>
                      <select
                        id="new-subject"
                        name="subject_id"
                        value={formData.subject_id}
                        onChange={handleInputChange}
                        className="subject-modal-input"
                        required
                        disabled={!modalSelectedStream}
                      >
                        <option value="">Select a subject</option>
                        {modalFilteredSubjects.map(subject => (
                          <option key={subject.id} value={subject.id}>{subject.name}</option>
                        ))}
                      </select>
                      {formErrors.subject_id && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.subject_id}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="new-name" className="subject-modal-form-label">Unit Name*</label>
                      <input
                        type="text"
                        id="new-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="subject-modal-input"
                        placeholder="Enter unit name"
                        required
                      />
                      {formErrors.name && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                      )}
                    </div>

                    <div className="col-span-2">
                      <label htmlFor="new-description" className="subject-modal-form-label">Description</label>
                      <textarea
                        id="new-description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                        className="subject-modal-textarea"
                        placeholder="Enter description"
                      />
                    </div>
                  </div>
                  <div className="subject-modal-helper px-4 mt-4">Fields marked with * are required</div>
                </div>
                {/* The footer is outside the scrollable area */}
              </form>
              {/* Modal Footer (fixed) */}
              <div className="subject-modal-actions" style={{ flex: 'none', borderTop: '1px solid #eee', paddingTop: 12, background: '#fff' }}>
                <button
                  type="button"
                  onClick={() => {
                    setOpenDialog(false);
                    resetForm();
                  }}
                  className="subject-modal-cancel-btn"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="create-unit-form"
                  className="subject-modal-save-btn transition"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : (selectedUnit ? 'Save Changes' : 'Save Unit')}
                </button>
              </div>
            </div>
          </div>
                 )}
       </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Delete Unit
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      Are you sure you want to delete the unit <span dangerouslySetInnerHTML={{ __html: deleteConfirmation.unitName }}/>? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition active:scale-95"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 sm:mt-0 sm:w-auto sm:text-sm transition active:scale-95"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default TeacherUnitsPage; 

